// Stores CRUD — list + create/edit modal + delete confirmation.

import React, { useEffect, useState } from 'react';
import { createStore, deleteStore, listStores, updateStore } from '../../services/adminService';
import DataTable from '../common/DataTable';
import ConfirmDialog from '../common/ConfirmDialog';
import PortalModal, { ModalActions } from '../common/PortalModal';
import Field from '../common/Field';

const BUSINESS_TYPES = ['grocery', 'pharmacy', 'restaurant', 'hardware', 'other'];

const blankForm = {
  name: '', whatsapp_number: '', business_type: 'grocery', address: '',
  owner_id: '', is_active: true, is_verified: false,
};

export default function AdminStores() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  // Client-side filters layered on top of the server search.
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);   // null = create, store object = edit
  const [form, setForm] = useState(blankForm);
  const [confirmDel, setConfirmDel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const reload = () => listStores({ q }).then(d => setRows(d.stores || [])).catch(() => {});
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const visible = rows.filter(s => {
    if (typeFilter && s.business_type !== typeFilter) return false;
    if (statusFilter === 'active' && !s.is_active) return false;
    if (statusFilter === 'inactive' && s.is_active) return false;
    if (ownerFilter === 'assigned' && !s.owner) return false;
    if (ownerFilter === 'unassigned' && s.owner) return false;
    if (tierFilter) {
      if (tierFilter === 'none' && s.subscription) return false;
      if (tierFilter !== 'none' && s.subscription?.tier !== tierFilter) return false;
    }
    return true;
  });

  const resetFilters = () => {
    setQ(''); setTypeFilter(''); setStatusFilter(''); setOwnerFilter(''); setTierFilter('');
  };
  const hasFilters = q || typeFilter || statusFilter || ownerFilter || tierFilter;

  const openCreate = () => { setEditing(null); setForm(blankForm); setShowForm(true); setError(''); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, whatsapp_number: s.whatsapp_number, business_type: s.business_type,
      address: s.address, owner_id: s.owner?.id || '',
      is_active: s.is_active, is_verified: s.is_verified,
    });
    setShowForm(true);
    setError('');
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      if (editing) await updateStore(editing.id, form);
      else await createStore(form);
      setShowForm(false);
      reload();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await deleteStore(confirmDel.id);
      setConfirmDel(null);
      reload();
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="portal-content-header">
        <h1>Stores</h1>
        <button type="button" className="portal-button" onClick={openCreate}>+ New store</button>
      </div>

      <div className="portal-filter-bar">
        <input
          type="search"
          className="portal-search"
          placeholder="Search by name or WhatsApp number"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <select className="portal-search" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {BUSINESS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="portal-search" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">All status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select className="portal-search" value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}>
          <option value="">Any owner</option>
          <option value="assigned">Owner assigned</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <select className="portal-search" value={tierFilter} onChange={e => setTierFilter(e.target.value)}>
          <option value="">Any subscription</option>
          <option value="none">No subscription</option>
          <option value="free">Free</option>
          <option value="basic">Basic</option>
          <option value="pro">Pro</option>
        </select>
        {hasFilters && <button type="button" className="portal-button secondary" onClick={resetFilters}>Clear</button>}
        <span className="meta">{visible.length} of {rows.length} stores</span>
      </div>

      <DataTable
        columns={[
          { key: 'name', label: 'Name', render: s => <strong>{s.name}</strong> },
          { key: 'business_type', label: 'Type' },
          { key: 'owner', label: 'Owner', render: s => s.owner ? (s.owner.email || s.owner.username) : <em style={{ color: 'var(--tudu-text-lo)' }}>unassigned</em> },
          { key: 'subscription', label: 'Subscription', render: s => s.subscription
              ? <span className={`badge badge-${s.subscription.status === 'active' ? 'active' : 'pending'}`}>{s.subscription.tier}</span>
              : <em style={{ color: 'var(--tudu-text-lo)' }}>none</em> },
          { key: 'flyers_count', label: 'Flyers' },
          { key: 'coupons_count', label: 'Coupons' },
          { key: 'is_active', label: 'Status', render: s => <span className={`badge badge-${s.is_active ? 'active' : 'inactive'}`}>{s.is_active ? 'Active' : 'Inactive'}</span> },
          {
            key: 'actions', label: '',
            render: s => (
              <div className="row-actions">
                <button type="button" className="portal-button secondary" onClick={() => openEdit(s)}>Edit</button>
                <button type="button" className="portal-button danger" onClick={() => setConfirmDel(s)}>Delete</button>
              </div>
            ),
          },
        ]}
        rows={visible}
        emptyMessage={hasFilters ? 'No stores match the current filters.' : 'No stores yet — create one to get started.'}
      />

      <PortalModal
        open={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? `Edit store: ${editing.name}` : 'New store'}
      >
        {error && <div className="portal-error">{error}</div>}
        <div className="portal-form-grid">
          <Field label="Name">
            <input className="portal-search" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="WhatsApp number">
            <input className="portal-search" value={form.whatsapp_number} onChange={e => setForm({ ...form, whatsapp_number: e.target.value })} />
          </Field>
          <Field label="Type">
            <select className="portal-search" value={form.business_type} onChange={e => setForm({ ...form, business_type: e.target.value })}>
              {BUSINESS_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </Field>
          <Field label="Owner user ID (optional)">
            <input className="portal-search" value={form.owner_id} onChange={e => setForm({ ...form, owner_id: e.target.value })} />
          </Field>
          <Field label="Address" full>
            <input className="portal-search" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
          </Field>
          <div className="full" style={{ display: 'flex', gap: 'var(--tudu-space-5)', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--tudu-text)', fontSize: 'var(--tudu-fs-sm)' }}>
              <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
              Active
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--tudu-text)', fontSize: 'var(--tudu-fs-sm)' }}>
              <input type="checkbox" checked={form.is_verified} onChange={e => setForm({ ...form, is_verified: e.target.checked })} />
              Verified
            </label>
          </div>
        </div>
        <ModalActions>
          <button type="button" className="portal-button secondary" onClick={() => setShowForm(false)}>Cancel</button>
          <button type="button" className="portal-button" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
        </ModalActions>
      </PortalModal>

      <ConfirmDialog
        open={!!confirmDel}
        title="Delete store?"
        message={`Delete "${confirmDel?.name}" and unlink its flyers/coupons? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={doDelete}
        onCancel={() => setConfirmDel(null)}
      />
    </>
  );
}
